import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createInspectionProgramItemService } from "../../../api/services/inspection-program-item.service";
import {
  ChallengeTableChallenge,
  ChallengeTableInspectionsProgramsVersionsItem,
} from "../../../api/types/dtos/challenge/ChallengeDto";

export type CellState = Record<string, CellStateValue>;

export type CellStateValue = Partial<
  Omit<
    ChallengeTableInspectionsProgramsVersionsItem,
    "id" | "programVersionId" | "inspectionProgramId"
  >
> & { _error?: boolean };

export interface UseChallengesControllerParams {
  data?: {
    challenges: ChallengeTableChallenge[];
    programVersionItems: ChallengeTableInspectionsProgramsVersionsItem[];
  };
  selectedOrganization?: { id?: string };
  onRefetch?: () => void;
}

type UpdateMutationVars = {
  organizationId: string;
  programVersionId: string;
  inspectionId: string;
  itemId: string;
  update: Partial<
    Pick<
      ChallengeTableInspectionsProgramsVersionsItem,
      "isActive" | "isApproved" | "comments" | "value" | "startDate" | "endDate"
    >
  >;
  batchId?: string;
  isLastInBatch?: boolean;
};

export const useChallengesController = ({
  data,
  selectedOrganization,
  onRefetch,
}: UseChallengesControllerParams) => {
  const [cellState, setCellState] = useState<CellState>({});
  const [updating, setUpdating] = useState<Record<string, boolean>>({});

  const [cellModalItem, setCellModalItem] =
    useState<ChallengeTableInspectionsProgramsVersionsItem | null>(null);
  const [cellModalCellKey, setCellModalCellKey] = useState<string>("");
  const [cellModalTemplateJson, setCellModalTemplateJson] = useState<
    string | null
  >(null);
  const [cellModalOpen, setCellModalOpen] = useState(false);

  const [rowModalItems, setRowModalItems] = useState<
    ChallengeTableInspectionsProgramsVersionsItem[]
  >([]);
  const [rowModalChallengeId, setRowModalChallengeId] = useState<string | null>(
    null,
  );
  const [rowModalOpen, setRowModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const pendingInvalidationRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingMutationsRef = useRef<Set<string>>(new Set());

  const getCurrentUtcIsoString = (): string => {
    const d = new Date();
    const iso = d.toISOString();
    return iso.replace(/\.(\d{3})Z$/, ".$1000Z");
  };

  const scheduleTableInvalidation = useCallback(
    (organizationId: string) => {
      if (pendingInvalidationRef.current) {
        clearTimeout(pendingInvalidationRef.current);
      }

      pendingInvalidationRef.current = setTimeout(() => {
        if (onRefetch) {
          onRefetch();
        } else {
          queryClient.invalidateQueries({
            queryKey: ["challengesTable", organizationId],
          });
        }
        pendingMutationsRef.current.clear();
        pendingInvalidationRef.current = null;
      }, 300);
    },
    [queryClient, onRefetch],
  );

  useEffect(() => {
    return () => {
      if (pendingInvalidationRef.current) {
        clearTimeout(pendingInvalidationRef.current);
      }
    };
  }, []);

  const updateMutation = useMutation({
    mutationFn: async (vars: UpdateMutationVars) => {
      const service = createInspectionProgramItemService(
        vars.organizationId,
        vars.programVersionId,
        vars.inspectionId,
      );
      return service
        .updateItemDetails(vars.itemId, vars.update)
        .then(
          (res) => res.data as ChallengeTableInspectionsProgramsVersionsItem,
        );
    },
    onMutate: async (variables: UpdateMutationVars) => {
      const orgId = variables.organizationId || selectedOrganization?.id;
      if (orgId) {
        await queryClient.cancelQueries({
          queryKey: ["challengesTable", orgId],
        });
      }

      if (!variables.batchId) {
        pendingMutationsRef.current.add(variables.itemId);
      }

      const { itemId } = variables;
      const cellKey = Object.keys(cellState).find((key) =>
        key.endsWith(`_${itemId.split("_").pop()}`),
      );
      const previousCellStateForItem = cellKey ? cellState[cellKey] : undefined;

      if (cellKey) {
        setCellState((s) => ({
          ...s,
          [cellKey]: {
            ...(s[cellKey] || {}),
            ...variables.update,
            _error: undefined,
          },
        }));
      }

      return { previousCellStateForItem, cellKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.cellKey) {
        setCellState((s) => ({
          ...s,
          [context.cellKey!]: {
            ...(s[context.cellKey!] || {}),
            ...context.previousCellStateForItem,
            _error: true,
          },
        }));
      }
    },
    onSuccess: (_apiResponse, _variables, context) => {
      if (context?.cellKey) {
        setCellState((s) => {
          const newCellState = { ...s };
          delete newCellState[context.cellKey!];
          return newCellState;
        });
      }
    },
    onSettled: (_apiResponse, _error, variables, context) => {
      if (variables.batchId) {
        if (variables.isLastInBatch) {
          const orgId = variables.organizationId || selectedOrganization?.id;
          if (orgId) {
            if (onRefetch) {
              onRefetch();
            } else if (!data) {
              queryClient.invalidateQueries({
                queryKey: ["challengesTable", variables.organizationId],
              });
            } else if (selectedOrganization?.id) {
              queryClient.invalidateQueries({
                queryKey: ["challengesTable", selectedOrganization.id],
              });
            }
          }
        }
      } else {
        const orgId = variables.organizationId || selectedOrganization?.id;
        if (orgId) {
          scheduleTableInvalidation(orgId);
        }
      }

      if (context?.cellKey) {
        const updateKey = `${context.cellKey}_${variables.itemId}`;
        setUpdating((u) => ({ ...u, [updateKey]: false }));
      } else {
        const genericUpdateKeySuffix = `_${variables.itemId.split("_").pop()}`;
        setUpdating((u) => {
          const newUpdating = { ...u };
          Object.keys(newUpdating).forEach((key) => {
            if (key.endsWith(genericUpdateKeySuffix)) {
              newUpdating[key] = false;
            }
          });
          return newUpdating;
        });
      }
    },
  });

  const handleOpenCellModal = useCallback(
    (
      modalItem: ChallengeTableInspectionsProgramsVersionsItem,
      key: string,
      formTemplateJson?: string,
    ) => {
      setCellModalItem(modalItem);
      setCellModalCellKey(key);
      setCellModalTemplateJson(formTemplateJson || null);
      setCellModalOpen(true);
    },
    [],
  );

  const handleOpenRowModal = useCallback(
    (row: any) => {
      const challenge = row._challengeData as ChallengeTableChallenge;
      if (!challenge) return;

      const itemsWithLocalState =
        challenge.inspectionsProgramsVersionsItems.map((item) => {
          const cellKey = `${challenge.inspectedOrgId}_${item.programVersionItemId}`;
          const localOverrides = cellState[cellKey] || {};
          return {
            ...item,
            ...localOverrides,
          } as ChallengeTableInspectionsProgramsVersionsItem;
        });
      setRowModalItems(itemsWithLocalState);
      setRowModalChallengeId(challenge.inspectedOrgId);
      setRowModalOpen(true);
    },
    [cellState],
  );

  const handleCellStateChange = useCallback(
    async (
      cellKeyFromParam: string,
      itemId: string,
      nextState: Partial<
        Pick<
          ChallengeTableInspectionsProgramsVersionsItem,
          "isActive" | "isApproved" | "comments" | "value"
        >
      >,
    ) => {
      let item: ChallengeTableInspectionsProgramsVersionsItem | undefined;
      if (data) {
        for (const challenge of data.challenges) {
          item = challenge.inspectionsProgramsVersionsItems.find(
            (i) => i.id === itemId,
          );
          if (item) break;
        }
      }
      if (!item) {
        return;
      }

      const updateKey = `${cellKeyFromParam}_${itemId}`;
      setUpdating((u) => ({ ...u, [updateKey]: true }));

      const prevIsActive = item.isActive;
      const prevIsApproved = item.isApproved;
      const nextIsActive = nextState.isActive ?? prevIsActive;
      const nextIsApproved = nextState.isApproved ?? prevIsApproved;

      const datePatch: Partial<
        Pick<
          ChallengeTableInspectionsProgramsVersionsItem,
          "startDate" | "endDate"
        >
      > = {};
      const nowIso = getCurrentUtcIsoString();

      if (!prevIsActive && nextIsActive && !nextIsApproved) {
        if (
          item.startDate === null ||
          item.startDate === undefined ||
          item.startDate === ""
        ) {
          datePatch.startDate = nowIso;
        }
      }
      if (!prevIsActive && nextIsActive && nextIsApproved) {
        if (
          item.startDate === null ||
          item.startDate === undefined ||
          item.startDate === ""
        ) {
          datePatch.startDate = nowIso;
        }
        if (
          item.endDate === null ||
          item.endDate === undefined ||
          item.endDate === ""
        ) {
          datePatch.endDate = nowIso;
        }
      }
      if (prevIsActive && !prevIsApproved && nextIsActive && nextIsApproved) {
        if (
          item.endDate === null ||
          item.endDate === undefined ||
          item.endDate === ""
        ) {
          datePatch.endDate = nowIso;
        }
      }

      const filteredDatePatch = Object.fromEntries(
        Object.entries(datePatch).filter(([_, v]) => v != null),
      );

      const combinedPayload = { ...item, ...nextState, ...filteredDatePatch };
      const finalFilteredPayload = Object.fromEntries(
        Object.entries(combinedPayload).filter(([_, v]) => v != null),
      );

      try {
        await updateMutation.mutateAsync({
          organizationId: selectedOrganization?.id || "",
          programVersionId: item.programVersionId,
          inspectionId: item.inspectionId,
          itemId,
          update: finalFilteredPayload,
        });
      } catch (_error: unknown) { /* ignore */ }
    },
    [data, selectedOrganization, updateMutation],
  );

  const handleSaveRowModal = useCallback(
    async (updatedItems: ChallengeTableInspectionsProgramsVersionsItem[]) => {
      if (!rowModalChallengeId || updatedItems.length === 0) return;

      const batchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      updatedItems.forEach((itemModal, index) => {
        const cellKey = `${rowModalChallengeId}_${itemModal.programVersionItemId}`;
        const updateKey = `${cellKey}_${itemModal.id}`;
        setUpdating((u) => ({ ...u, [updateKey]: true }));

        const updatePayload: Partial<
          Pick<
            ChallengeTableInspectionsProgramsVersionsItem,
            "isActive" | "isApproved" | "comments" | "value"
          >
        > = {
          isActive: itemModal.isActive,
          isApproved: itemModal.isApproved,
          comments: itemModal.comments,
          value: itemModal.value,
        };

        let originalItem:
          | ChallengeTableInspectionsProgramsVersionsItem
          | undefined;
        if (data) {
          for (const challenge of data.challenges) {
            originalItem = challenge.inspectionsProgramsVersionsItems.find(
              (i) => i.id === itemModal.id,
            );
            if (originalItem) break;
          }
        }

        if (!originalItem) {
          setUpdating((u) => ({ ...u, [updateKey]: false }));
          return;
        }

        const prevIsActive = originalItem.isActive;
        const prevIsApproved = originalItem.isApproved;
        const nextIsActive = itemModal.isActive;
        const nextIsApproved = itemModal.isApproved;

        const datePatch: Partial<
          Pick<
            ChallengeTableInspectionsProgramsVersionsItem,
            "startDate" | "endDate"
          >
        > = {};
        const nowIso = getCurrentUtcIsoString();

        if (!prevIsActive && nextIsActive && !nextIsApproved) {
          if (
            originalItem.startDate === null ||
            originalItem.startDate === undefined ||
            originalItem.startDate === ""
          ) {
            datePatch.startDate = nowIso;
          }
        }
        if (!prevIsActive && nextIsActive && nextIsApproved) {
          if (
            originalItem.startDate === null ||
            originalItem.startDate === undefined ||
            originalItem.startDate === ""
          ) {
            datePatch.startDate = nowIso;
          }
          if (
            originalItem.endDate === null ||
            originalItem.endDate === undefined ||
            originalItem.endDate === ""
          ) {
            datePatch.endDate = nowIso;
          }
        }
        if (prevIsActive && !prevIsApproved && nextIsActive && nextIsApproved) {
          if (
            originalItem.endDate === null ||
            originalItem.endDate === undefined ||
            originalItem.endDate === ""
          ) {
            datePatch.endDate = nowIso;
          }
        }

        const filteredDatePatch = Object.fromEntries(
          Object.entries(datePatch).filter(([_, v]) => v != null),
        );

        const ALLOWED_UPDATE_KEYS = [
          "isActive",
          "isApproved",
          "comments",
          "value",
          "startDate",
          "endDate",
        ] as const;
        const finalFilteredPayload = Object.fromEntries(
          Object.entries({
            ...originalItem,
            ...itemModal,
            ...filteredDatePatch,
          }).filter(
            ([k, v]) => ALLOWED_UPDATE_KEYS.includes(k as any) && v != null,
          ),
        );

        const isLastInBatch = index === updatedItems.length - 1;

        updateMutation.mutate({
          organizationId: selectedOrganization?.id || "",
          programVersionId: originalItem.programVersionId,
          inspectionId: originalItem.inspectionId,
          itemId: itemModal.id,
          update: finalFilteredPayload,
          batchId,
          isLastInBatch,
        });
      });
    },
    [rowModalChallengeId, data, selectedOrganization, updateMutation],
  );

  return {
    cellState,
    setCellState,
    updating,
    setUpdating,
    cellModalItem,
    setCellModalItem,
    cellModalCellKey,
    setCellModalCellKey,
    cellModalTemplateJson,
    setCellModalTemplateJson,
    cellModalOpen,
    setCellModalOpen,
    rowModalItems,
    setRowModalItems,
    rowModalChallengeId,
    setRowModalChallengeId,
    rowModalOpen,
    setRowModalOpen,
    handleCellStateChange,
    handleOpenCellModal,
    handleOpenRowModal,
    handleSaveRowModal,
  };
};
